import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { t, font, Button } from "@realtyflows/ui";
import { getPackets, createPacket, deletePacket } from "../api";
import useResponsive from "../hooks/useResponsive";

const STATUS_COLORS = {
  draft: t.textMuted, filling: t.amber, ready: t.emerald, sent: "#3b82f6",
  partially_signed: "#a855f7", signed: t.emerald, voided: t.coral,
};
const STATUS_LABELS = {
  draft: "Draft", filling: "Filling", ready: "Ready", sent: "Sent",
  partially_signed: "Partially Signed", signed: "Signed", voided: "Voided",
};

export default function PacketList() {
  const navigate = useNavigate();
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;
  const [packets, setPackets] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => { getPackets().then(setPackets).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    const packet = await createPacket({ name: "" });
    navigate(`/packets/${packet.id}`);
  };

  const handleDelete = async (id) => {
    await deletePacket(id);
    load();
  };

  return (
    <div style={{ height: "100%", overflow: "auto", background: t.pageBg }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: isMobile ? 16 : 32 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <div>
            <span onClick={() => navigate("/contracts")} style={{ fontSize: 12, color: t.textMuted, cursor: "pointer", fontFamily: font.body }}>&larr; Back to Contracts</span>
            <h1 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 600, color: t.navyLight, fontFamily: font.display, marginTop: 4 }}>Transaction Packets</h1>
            <p style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body, marginTop: 4 }}>
              Group forms into packets, fill them out, and send for signing as one envelope.
            </p>
          </div>
          <Button variant="primary" onClick={handleCreate}>+ New Packet</Button>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 60, color: t.textMuted, fontFamily: font.body }}>Loading...</div>
        ) : packets.length === 0 ? (
          <div style={{
            textAlign: "center", padding: 60, background: t.surface, border: `1.5px dashed ${t.border}`, borderRadius: 12,
          }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>{"\uD83D\uDCC2"}</div>
            <div style={{ fontSize: 15, fontWeight: 500, color: t.textPrimary, fontFamily: font.body, marginBottom: 6 }}>No packets yet</div>
            <div style={{ fontSize: 13, color: t.textMuted, fontFamily: font.body, marginBottom: 16 }}>
              Create a packet to group forms for a client or transaction.
            </div>
            <Button variant="primary" onClick={handleCreate}>Create First Packet</Button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {packets.map(p => (
              <div key={p.id} onClick={() => navigate(`/packets/${p.id}`)} style={{
                display: "flex", alignItems: "center", gap: 16, padding: isMobile ? "14px 12px" : "16px 20px",
                background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10,
                cursor: "pointer", transition: "all 0.15s",
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = `${t.gold}40`; e.currentTarget.style.boxShadow = `0 2px 12px ${t.gold}10`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.boxShadow = "none"; }}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: 10, background: `${t.navyLight}10`, flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
                }}>{"\uD83D\uDCC2"}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: t.textPrimary, fontFamily: font.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.name}
                  </div>
                  <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, marginTop: 2 }}>
                    {p.client_name || "No client"}{p.property_address ? ` — ${p.property_address}` : ""}
                    {" — "}{p.form_count || 0} forms ({p.forms_complete || 0} complete)
                  </div>
                </div>
                <div style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase",
                  color: STATUS_COLORS[p.status] || t.textMuted, fontFamily: font.mono,
                  padding: "4px 8px", borderRadius: 4, background: (STATUS_COLORS[p.status] || t.textMuted) + "15",
                }}>{STATUS_LABELS[p.status] || p.status}</div>
                {p.status === "draft" && (
                  <button onClick={e => { e.stopPropagation(); handleDelete(p.id); }} style={{
                    background: "none", border: "none", fontSize: 16, color: t.textMuted, cursor: "pointer", padding: "4px 8px",
                  }} title="Delete draft">&times;</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
